import React, { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { useNavigate } from "react-router-dom";
import { useOrganization } from "@clerk/clerk-react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { useAppAuth } from "@/contexts/AuthContext";
import {
  Users,
  Shield,
  Crown,
  UserCog,
  User,
  Loader2,
  Trash2,
  HelpCircle,
  Check,
  ChevronDown,
  Settings2,
  UserPlus,
  Mail,
  Sparkles,
  MapPin,
  Search,
  Plus,
  X,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { EmptyState } from "@/components/ui/empty-state";

// Role flags configuration
interface RoleFlags {
  canManageTeam?: boolean;
  canManageBilling?: boolean;
  canGrantDocumentAccess?: boolean;
  canAccessAutomation?: boolean;
  canManageLeadsAndBids?: boolean;
  canManageResources?: boolean;
}

// Define which role flags are available for each user role
// Higher roles can grant more flags
type UserRole = "admin" | "manager" | "member";

// Role flag metadata for display
const roleFlagConfig: {
  key: keyof RoleFlags;
  label: string;
  description: string;
  permission: string;
  availableForRoles: UserRole[]; // Which user roles can have this flag enabled
}[] = [
  {
    key: "canManageTeam",
    label: "Team Management",
    description: "Invite and manage team members",
    permission: "team:write, team:invite",
    availableForRoles: ["admin"], // Only admins can be granted team management
  },
  {
    key: "canManageBilling",
    label: "Billing Access",
    description: "View and manage billing settings",
    permission: "organization:billing",
    availableForRoles: ["admin"], // Only admins can be granted billing access
  },
  {
    key: "canGrantDocumentAccess",
    label: "Document Access Control",
    description: "Grant others access to documents",
    permission: "documents:grant_access",
    availableForRoles: ["admin", "manager", "member"], // All roles can be granted document access
  },
  {
    key: "canAccessAutomation",
    label: "Automation Hub Access",
    description: "Grant access to Automation Hub workflows",
    permission: "automation:read",
    availableForRoles: ["manager", "member"],
  },
  {
    key: "canManageLeadsAndBids",
    label: "Full Access to All Leads & Bids",
    description: "Access all organization leads and bids (instead of self-created only)",
    permission: "leads:read_all, bids:read_all",
    availableForRoles: ["manager", "member"],
  },
  {
    key: "canManageResources",
    label: "Manage Team Resources",
    description: "Create, edit, and delete employees, subcontractors, equipment, and supplies",
    permission: "team:write, clients:write",
    availableForRoles: ["manager", "member"],
  },
];

// Helper function to get available flags for a user's role
function getAvailableFlagsForRole(role: string | undefined): typeof roleFlagConfig {
  const userRole = (role || "member") as UserRole;
  return roleFlagConfig.filter((flag) => flag.availableForRoles.includes(userRole));
}

// User type for the list
interface OrgUser {
  _id: Id<"users">;
  name?: string;
  email?: string;
  image?: string;
  role?: string;
  roleFlags?: RoleFlags;
  assignedLocations?: Id<"serviceLocations">[];
  isOwner?: boolean;
  joinedAt?: number;
}

// Role options for dropdown
type RoleOption = "admin" | "manager" | "member";
const roleOptions: { value: RoleOption; label: string }[] = [
  { value: "admin", label: "Admin" },
  { value: "manager", label: "Manager" },
  { value: "member", label: "Member" },
];

// Role badge configuration
const roleBadgeConfig: Record<
  string,
  { label: string; icon: React.ElementType; className: string }
> = {
  owner: {
    label: "Owner",
    icon: Crown,
    className: "bg-amber-100 text-amber-800 hover:bg-amber-200",
  },
  admin: {
    label: "Admin",
    icon: Shield,
    className: "bg-purple-100 text-purple-800 hover:bg-purple-200",
  },
  manager: {
    label: "Manager",
    icon: UserCog,
    className: "bg-blue-100 text-blue-800 hover:bg-blue-200",
  },
  member: {
    label: "Member",
    icon: User,
    className: "bg-gray-100 text-gray-800 hover:bg-gray-200",
  },
};

function getRoleBadge(role: string | undefined) {
  const config = roleBadgeConfig[role || "member"] || roleBadgeConfig.member;
  const Icon = config.icon;

  return (
    <Badge variant="secondary" className={cn("gap-1 font-medium", config.className)}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

function formatDate(timestamp: number | undefined): string {
  if (!timestamp) return "—";
  return new Date(timestamp).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function UsersRolesPage() {
  const { toast } = useToast();
  const { hasPermission } = useAppAuth();
  const { organization } = useOrganization();
  const navigate = useNavigate();
  const users = useQuery(api.users.list);
  const currentUser = useQuery(api.users.getCurrentUser);
  const subscriptionData = useQuery(api.subscriptions.getOrganizationSubscription, {});
  const locations = useQuery(api.serviceLocations.getServiceLocations) || [];
  const updateUserRole = useMutation(api.users.updateUserRole);
  const removeUser = useMutation(api.users.removeUserFromOrganization);

  // Check if user has a team plan (can invite users)
  const currentPlanType = subscriptionData?.organization?.planType || "basic";
  const hasTeamPlan = currentPlanType.startsWith("team");

  const [updatingUserId, setUpdatingUserId] = useState<Id<"users"> | null>(null);
  const [removingUser, setRemovingUser] = useState<OrgUser | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showPermissionsSheet, setShowPermissionsSheet] = useState(false);
  const [expandedFlagsUserId, setExpandedFlagsUserId] = useState<Id<"users"> | null>(null);
  const [updatingFlagKey, setUpdatingFlagKey] = useState<string | null>(null);

  // Invite state
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showUpgradeAlert, setShowUpgradeAlert] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [isInviting, setIsInviting] = useState(false);

  // Check permissions
  const canEditRoles = hasPermission("team:write");
  const canRemoveUsers = hasPermission("team:remove");
  const canInviteUsers = hasPermission("team:invite");

  // Handle location assignment change for members
  const handleLocationAssignmentChange = async (
    user: OrgUser,
    locationId: Id<"serviceLocations">,
    isAssigned: boolean
  ) => {
    try {
      const currentAssigned = user.assignedLocations || [];
      const updated = isAssigned
        ? [...currentAssigned, locationId]
        : currentAssigned.filter((id) => id !== locationId);

      await updateUserRole({
        userId: user._id,
        newRole: (user.role || "member") as "owner" | "admin" | "manager" | "member" | "viewer",
        roleFlags: user.roleFlags,
        assignedLocations: updated,
      });

      toast({
        title: "Assigned locations updated",
        description: `Service location assignments updated for ${user.name || user.email || "this user"}.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update location assignment",
        variant: "destructive",
      });
    }
  };

  // Handle bulk location assignment change for members
  const handleBulkLocationAssignmentChange = async (
    user: OrgUser,
    locationIds: Id<"serviceLocations">[]
  ) => {
    try {
      await updateUserRole({
        userId: user._id,
        newRole: (user.role || "member") as "owner" | "admin" | "manager" | "member" | "viewer",
        roleFlags: user.roleFlags,
        assignedLocations: locationIds,
      });

      toast({
        title: "Assigned locations updated",
        description: `Service location assignments updated for ${user.name || user.email || "this user"}.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update location assignments",
        variant: "destructive",
      });
    }
  };

  // Handle invite user
  const handleInviteUser = async () => {
    if (!inviteEmail.trim() || !organization) return;

    setIsInviting(true);
    try {
      await organization.inviteMember({
        emailAddress: inviteEmail.trim(),
        role: "basic_member",
      });
      toast({
        title: "Invitation sent",
        description: `An invitation has been sent to ${inviteEmail}.`,
      });
      setInviteEmail("");
      setShowInviteDialog(false);
    } catch (error) {
      toast({
        title: "Failed to send invitation",
        description:
          error instanceof Error
            ? error.message
            : "An error occurred while sending the invitation.",
        variant: "destructive",
      });
    } finally {
      setIsInviting(false);
    }
  };

  // Handle role flag toggle
  const handleRoleFlagChange = async (
    user: OrgUser,
    flagKey: keyof RoleFlags,
    newValue: boolean
  ) => {
    const flagDisplayKey = `${user._id}-${flagKey}`;
    setUpdatingFlagKey(flagDisplayKey);
    try {
      const currentFlags = (user.roleFlags || {}) as RoleFlags;
      const updatedFlags = {
        ...currentFlags,
        [flagKey]: newValue,
      };

      await updateUserRole({
        userId: user._id,
        newRole: (user.role || "member") as "owner" | "admin" | "manager" | "member" | "viewer",
        roleFlags: updatedFlags,
      });

      const flagLabel = roleFlagConfig.find((f) => f.key === flagKey)?.label || flagKey;
      toast({
        title: newValue ? "Permission enabled" : "Permission disabled",
        description: `${flagLabel} has been ${newValue ? "enabled" : "disabled"} for ${user.name || user.email || "this user"}.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update permission",
        variant: "destructive",
      });
    } finally {
      setUpdatingFlagKey(null);
    }
  };

  const handleRoleChange = async (userId: Id<"users">, newRole: RoleOption) => {
    setUpdatingUserId(userId);
    try {
      await updateUserRole({
        userId,
        newRole,
      });
      toast({
        title: "Role updated",
        description: `User role has been updated to ${newRole}.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update role",
        variant: "destructive",
      });
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleRemoveUser = async () => {
    if (!removingUser) return;

    setIsRemoving(true);
    try {
      await removeUser({ userId: removingUser._id });
      toast({
        title: "User removed",
        description: `${removingUser.name || removingUser.email || "User"} has been removed from the organization.`,
      });
      setRemovingUser(null);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to remove user",
        variant: "destructive",
      });
    } finally {
      setIsRemoving(false);
    }
  };

  // Determine if a user's role can be edited
  const canEditUserRole = (user: OrgUser): boolean => {
    if (!canEditRoles) return false;
    if (user.isOwner || user.role === "owner") return false;
    if (currentUser && user._id === currentUser._id) return false;
    return true;
  };

  // Determine if a user can be removed
  const canRemoveUser = (user: OrgUser): boolean => {
    if (!canRemoveUsers) return false;
    if (user.isOwner || user.role === "owner") return false;
    if (currentUser && user._id === currentUser._id) return false;
    return true;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header Card */}
        <Card className="p-6">
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-blue-100 p-2">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Users & Roles</h2>
                  <p className="text-sm text-gray-500">
                    View and manage team members in your organization
                  </p>
                </div>
              </div>
              {canInviteUsers && (
                <Button
                  onClick={() => {
                    if (hasTeamPlan) {
                      setShowInviteDialog(true);
                    } else {
                      setShowUpgradeAlert(true);
                    }
                  }}
                  className="gap-2"
                >
                  <UserPlus className="h-4 w-4" />
                  Invite Member
                </Button>
              )}
            </div>

            {/* Role Legend */}
            <div className="flex flex-wrap items-center gap-3 border-t border-gray-200 pt-4">
              <span className="mr-2 text-sm text-gray-500">Role types:</span>
              {Object.entries(roleBadgeConfig).map(([role, config]) => {
                const Icon = config.icon;
                return (
                  <Badge
                    key={role}
                    variant="secondary"
                    className={cn("gap-1 font-medium", config.className)}
                  >
                    <Icon className="h-3 w-3" />
                    {config.label}
                  </Badge>
                );
              })}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-gray-500 hover:text-blue-600"
                onClick={() => setShowPermissionsSheet(true)}
              >
                <HelpCircle className="mr-1 h-4 w-4" />
                <span className="text-xs">View permissions</span>
              </Button>
            </div>
          </div>
        </Card>

        {/* Users List Card */}
        <Card className="p-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Team Members</h3>
              <p className="text-sm text-gray-500">
                {users?.length || 0} member(s) in your organization
              </p>
            </div>

            {users === undefined ? (
              <div className="py-8 text-center text-gray-500">Loading team members...</div>
            ) : users.length === 0 ? (
              <EmptyState
                icon={Users}
                title="No team members found"
                description="Invite team members to get started."
              />
            ) : (
              <div className="overflow-hidden rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Joined</TableHead>
                      {(canEditRoles || canRemoveUsers) && (
                        <TableHead className="w-[80px]">Actions</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user: OrgUser) => {
                      const isUpdating = updatingUserId === user._id;
                      const editable = canEditUserRole(user);
                      const removable = canRemoveUser(user);
                      const isOwner = user.isOwner || user.role === "owner";

                      return (
                        <React.Fragment key={user._id}>
                          <TableRow
                            className={expandedFlagsUserId === user._id ? "border-b-0" : ""}
                          >
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                {user.image ? (
                                  <img
                                    src={user.image}
                                    alt={user.name || "User"}
                                    className="h-8 w-8 rounded-full object-cover"
                                  />
                                ) : (
                                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200">
                                    <User className="h-4 w-4 text-gray-500" />
                                  </div>
                                )}
                                <span>{user.name || "Unnamed User"}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-gray-600">{user.email || "—"}</TableCell>
                            <TableCell>
                              {isOwner ? (
                                getRoleBadge("owner")
                              ) : editable ? (
                                <div className="flex items-center gap-2">
                                  <Select
                                    value={user.role || "member"}
                                    onValueChange={(value) =>
                                      handleRoleChange(user._id, value as RoleOption)
                                    }
                                    disabled={isUpdating}
                                  >
                                    <SelectTrigger className="h-8 w-[140px]">
                                      {isUpdating ? (
                                        <div className="flex items-center gap-2">
                                          <Loader2 className="h-3 w-3 animate-spin" />
                                          <span>Updating...</span>
                                        </div>
                                      ) : (
                                        <SelectValue />
                                      )}
                                    </SelectTrigger>
                                    <SelectContent>
                                      {roleOptions.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                          {option.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              ) : (
                                getRoleBadge(user.role)
                              )}
                            </TableCell>
                            <TableCell className="text-gray-500">
                              {formatDate(user.joinedAt)}
                            </TableCell>
                            {(canEditRoles || canRemoveUsers) && (
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  {/* Role Flags Toggle Button - only for non-owners */}
                                  {editable && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className={cn(
                                        "h-8 w-8 p-0 text-gray-500 hover:bg-blue-50 hover:text-blue-600",
                                        expandedFlagsUserId === user._id &&
                                          "bg-blue-50 text-blue-600"
                                      )}
                                      onClick={() =>
                                        setExpandedFlagsUserId(
                                          expandedFlagsUserId === user._id ? null : user._id
                                        )
                                      }
                                      title="Customize permissions"
                                    >
                                      <Settings2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {removable && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 text-gray-500 hover:bg-red-50 hover:text-red-600"
                                      onClick={() => setRemovingUser(user)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                          {/* Expandable Role Flags Section */}
                          {expandedFlagsUserId === user._id && !isOwner && (
                            <TableRow className="bg-gray-50/50 hover:bg-gray-50/50">
                              <TableCell colSpan={5} className="py-4">
                                <div className="ml-10 space-y-3">
                                  <div className="mb-3 flex items-center gap-2">
                                    <Settings2 className="h-4 w-4 text-gray-500" />
                                    <span className="text-sm font-medium text-gray-700">
                                      Custom Permissions for {user.name || user.email}
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                    {getAvailableFlagsForRole(user.role).length === 0 ? (
                                      <div className="col-span-2 py-4 text-center text-sm text-gray-500">
                                        No additional permissions available for the{" "}
                                        {user.role || "member"} role.
                                        <br />
                                        <span className="text-xs text-gray-400">
                                          Promote to a higher role to unlock more permissions.
                                        </span>
                                      </div>
                                    ) : (
                                      getAvailableFlagsForRole(user.role).map((flag) => {
                                        const flagKey = `${user._id}-${flag.key}`;
                                        const isUpdatingFlag = updatingFlagKey === flagKey;
                                        const isEnabled = user.roleFlags?.[flag.key] === true;

                                        return (
                                          <div
                                            key={flag.key}
                                            className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3"
                                          >
                                            <div className="flex-1">
                                              <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-gray-900">
                                                  {flag.label}
                                                </span>
                                                {isUpdatingFlag && (
                                                  <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
                                                )}
                                              </div>
                                              <p className="mt-0.5 text-xs text-gray-500">
                                                {flag.description}
                                              </p>
                                              <p className="mt-0.5 text-xs text-gray-400">
                                                Grants:{" "}
                                                <code className="rounded bg-gray-100 px-1">
                                                  {flag.permission}
                                                </code>
                                              </p>
                                            </div>
                                            <Switch
                                              checked={isEnabled}
                                              onCheckedChange={(checked) =>
                                                handleRoleFlagChange(user, flag.key, checked)
                                              }
                                              disabled={isUpdatingFlag}
                                            />
                                          </div>
                                        );
                                      })
                                    )}
                                  </div>

                                  {/* Assigned Service Locations Component */}
                                  <LocationAssignmentPicker
                                    user={user}
                                    locations={locations}
                                    onAssignmentChange={handleLocationAssignmentChange}
                                    onBulkAssignmentChange={handleBulkLocationAssignmentChange}
                                  />

                                  <p className="mt-2 text-xs text-gray-400">
                                    These permissions are in addition to the base permissions for
                                    the <strong>{user.role || "member"}</strong> role.
                                  </p>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Remove User Confirmation Dialog */}
      <AlertDialog open={!!removingUser} onOpenChange={() => setRemovingUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove team member?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove{" "}
              <span className="font-medium text-gray-900">
                {removingUser?.name || removingUser?.email || "this user"}
              </span>{" "}
              from the organization? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveUser}
              disabled={isRemoving}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {isRemoving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Removing...
                </>
              ) : (
                "Remove"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Role Permissions Sheet */}
      <Sheet open={showPermissionsSheet} onOpenChange={setShowPermissionsSheet}>
        <SheetContent className="w-[400px] overflow-y-auto sm:w-[540px]">
          <SheetHeader>
            <SheetTitle>Role Permissions</SheetTitle>
            <SheetDescription>
              Each role tier includes all permissions from the tiers below it.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Owner Tier */}
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="mb-3 flex items-center gap-2">
                <Crown className="h-5 w-5 text-amber-600" />
                <h3 className="font-semibold text-amber-900">Owner</h3>
                <Badge variant="secondary" className="bg-amber-100 text-xs text-amber-800">
                  Tier 4
                </Badge>
              </div>
              <p className="mb-3 text-sm text-amber-800">
                Full organizational control including billing and ownership rights.
              </p>
              <ul className="space-y-1.5 text-sm text-amber-900">
                <li className="flex items-center gap-2">
                  <Check className="h-3.5 w-3.5 text-amber-600" />
                  <span>All Admin permissions</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-3.5 w-3.5 text-amber-600" />
                  <span>Billing & subscription management</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-3.5 w-3.5 text-amber-600" />
                  <span>Transfer organization ownership</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-3.5 w-3.5 text-amber-600" />
                  <span>Delete organization</span>
                </li>
              </ul>
            </div>

            {/* Admin Tier */}
            <div className="rounded-lg border border-purple-200 bg-purple-50/70 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-purple-700" />
                  <h3 className="font-semibold text-purple-950">Admin</h3>
                  <Badge variant="secondary" className="bg-purple-100 text-xs text-purple-800 font-medium">
                    Administrator
                  </Badge>
                </div>
              </div>
              <p className="text-xs text-purple-800">
                Full operational control over organization data, team members, and system features.
              </p>

              {/* Allowed Access Section */}
              <div className="space-y-1.5 rounded-md bg-white p-3 border border-emerald-200 shadow-sm">
                <span className="text-xs font-semibold text-emerald-800 flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> Allowed Access:
                </span>
                <ul className="space-y-1.5 text-xs text-slate-700">
                  <li className="flex items-start gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                    <span><strong>Team Management:</strong> Invite, remove, and change user roles across the organization.</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                    <span><strong>Full Operations:</strong> View & manage all clients, service locations, bids, leads, contracts, and service projects.</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                    <span><strong>Full Resource Control:</strong> Create, edit, and delete employees, subcontractors, equipment, and supplies.</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                    <span><strong>Automations & AI Knowledge Base:</strong> Full access to Automation Hub, AI Knowledge Base, and Ratings & Reviews.</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                    <span><strong>Organization Settings:</strong> Manage organization profile, branding, and integrations.</span>
                  </li>
                </ul>
              </div>

              {/* Restricted Access Section */}
              <div className="space-y-1.5 rounded-md bg-white p-3 border border-rose-200 shadow-sm">
                <span className="text-xs font-semibold text-rose-800 flex items-center gap-1.5">
                  <XCircle className="h-4 w-4 text-rose-600 shrink-0" /> Restricted (Reserved for Owner):
                </span>
                <ul className="space-y-1.5 text-xs text-slate-700">
                  <li className="flex items-start gap-1.5">
                    <X className="h-3.5 w-3.5 text-rose-500 shrink-0 mt-0.5" />
                    <span><strong>Billing & Subscriptions:</strong> Cannot change subscription plans, view invoices, or update payment methods (unless granted <em>Billing Access</em> toggle).</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <X className="h-3.5 w-3.5 text-rose-500 shrink-0 mt-0.5" />
                    <span><strong>Organization Ownership:</strong> Cannot transfer organization ownership or delete the organization account.</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Manager Tier */}
            <div className="rounded-lg border border-blue-200 bg-blue-50/70 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserCog className="h-5 w-5 text-blue-700" />
                  <h3 className="font-semibold text-blue-950">Manager</h3>
                  <Badge variant="secondary" className="bg-blue-100 text-xs text-blue-800 font-medium">
                    Operational Manager
                  </Badge>
                </div>
              </div>
              <p className="text-xs text-blue-800">
                Operational management role with client, contract, and self-created lead/bid access. Customizable via custom permission toggles.
              </p>

              {/* Allowed Access Section */}
              <div className="space-y-1.5 rounded-md bg-white p-3 border border-emerald-200 shadow-sm">
                <span className="text-xs font-semibold text-emerald-800 flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> Allowed Access:
                </span>
                <ul className="space-y-1.5 text-xs text-slate-700">
                  <li className="flex items-start gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                    <span><strong>Clients & Locations:</strong> Create & edit clients, service locations, and onboarding.</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                    <span><strong>Contracts & Applications:</strong> Create/edit contracts and review subcontractor applications.</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                    <span><strong>Self-Created Records:</strong> View & manage self-created leads, bids, documents, and service projects.</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                    <span><strong>Read-Only Resources:</strong> Read-only view of employees, subcontractors, equipment, and supplies.</span>
                  </li>
                </ul>
              </div>

              {/* Restricted Access Section */}
              <div className="space-y-1.5 rounded-md bg-white p-3 border border-rose-200 shadow-sm">
                <span className="text-xs font-semibold text-rose-800 flex items-center gap-1.5">
                  <XCircle className="h-4 w-4 text-rose-600 shrink-0" /> Strictly Restricted (Default):
                </span>
                <ul className="space-y-1.5 text-xs text-slate-700">
                  <li className="flex items-start gap-1.5">
                    <X className="h-3.5 w-3.5 text-rose-500 shrink-0 mt-0.5" />
                    <span><strong>Automation Hub:</strong> No access (can be unlocked via <em>Automation Hub Access</em> toggle).</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <X className="h-3.5 w-3.5 text-rose-500 shrink-0 mt-0.5" />
                    <span><strong>AI Knowledge Base & Settings:</strong> Cannot access AI Knowledge Base, organization settings, or billing.</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <X className="h-3.5 w-3.5 text-rose-500 shrink-0 mt-0.5" />
                    <span><strong>Org-Wide Leads & Bids:</strong> Scoped to self-created leads/bids (can be unlocked via <em>Full Access to All Leads & Bids</em> toggle).</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <X className="h-3.5 w-3.5 text-rose-500 shrink-0 mt-0.5" />
                    <span><strong>Resource Editing:</strong> Cannot add/edit employees, subcontractors, or inventory (can be unlocked via <em>Manage Team Resources</em> toggle).</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Member Tier (Field Supervisor) */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5 text-slate-700" />
                  <h3 className="font-semibold text-slate-900">Member</h3>
                  <Badge variant="secondary" className="bg-slate-200 text-xs text-slate-800 font-medium">
                    Field Supervisor
                  </Badge>
                </div>
              </div>
              <p className="text-xs text-slate-600">
                Operational field role scoped strictly to assigned service locations and owned records.
              </p>

              {/* Allowed Access Section */}
              <div className="space-y-1.5 rounded-md bg-white p-3 border border-emerald-200 shadow-sm">
                <span className="text-xs font-semibold text-emerald-800 flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> Allowed Access:
                </span>
                <ul className="space-y-1.5 text-xs text-slate-700">
                  <li className="flex items-start gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                    <span><strong>Assigned Service Locations:</strong> View & manage locations explicitly assigned by admin/manager.</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                    <span><strong>Service Projects:</strong> View location projects; edit & delete self-created projects.</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                    <span><strong>Created Documents:</strong> View & manage documents created/uploaded by self.</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                    <span><strong>Read-Only Operational Views:</strong> Read-only view of team roster, employees, subcontractors, equipment, and supplies.</span>
                  </li>
                </ul>
              </div>

              {/* Restricted Access Section */}
              <div className="space-y-1.5 rounded-md bg-white p-3 border border-rose-200 shadow-sm">
                <span className="text-xs font-semibold text-rose-800 flex items-center gap-1.5">
                  <XCircle className="h-4 w-4 text-rose-600 shrink-0" /> Strictly Restricted (No Access):
                </span>
                <ul className="space-y-1.5 text-xs text-slate-700">
                  <li className="flex items-start gap-1.5">
                    <X className="h-3.5 w-3.5 text-rose-500 shrink-0 mt-0.5" />
                    <span><strong>Bids & Pricing Info:</strong> No access to bids, proposals, cost estimates, or profitability stats.</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <X className="h-3.5 w-3.5 text-rose-500 shrink-0 mt-0.5" />
                    <span><strong>Leads & Opportunity Pipeline:</strong> No access to leads, CRM, or sales opportunities.</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <X className="h-3.5 w-3.5 text-rose-500 shrink-0 mt-0.5" />
                    <span><strong>All Clients List & Onboarding:</strong> Cannot access overall client database or location onboarding.</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <X className="h-3.5 w-3.5 text-rose-500 shrink-0 mt-0.5" />
                    <span><strong>Settings & Connections:</strong> Cannot access organization settings, billing, or integrations (Gmail, Calendar, etc.).</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <X className="h-3.5 w-3.5 text-rose-500 shrink-0 mt-0.5" />
                    <span><strong>Automation & Knowledge Base:</strong> No access to Automation Hub, Ratings & Reviews, or AI Knowledge Base.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Invite Member Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-blue-600" />
              Invite Team Member
            </DialogTitle>
            <DialogDescription>
              Send an invitation email to add a new member to your organization. They will receive
              an email with instructions to join.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="email">Email address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="colleague@company.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="pl-10"
                  disabled={isInviting}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && inviteEmail.trim()) {
                      handleInviteUser();
                    }
                  }}
                />
              </div>
              <p className="text-xs text-gray-500">
                New members will join with the <strong>Member</strong> role by default. You can
                change their role after they join.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowInviteDialog(false);
                setInviteEmail("");
              }}
              disabled={isInviting}
            >
              Cancel
            </Button>
            <Button onClick={handleInviteUser} disabled={!inviteEmail.trim() || isInviting}>
              {isInviting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Send Invitation
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade Required Alert for Non-Team Plans */}
      <AlertDialog open={showUpgradeAlert} onOpenChange={setShowUpgradeAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="mb-2 flex items-center gap-3">
              <div className="rounded-lg bg-amber-100 p-2">
                <Sparkles className="h-5 w-5 text-amber-600" />
              </div>
              <AlertDialogTitle className="text-xl">Team Plan Required</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-base">
              Inviting team members is available on <strong>Team plans</strong> only. Upgrade to a
              Team plan to unlock multi-user collaboration with pooled credits and unlimited field
              users.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="my-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <p className="mb-2 text-sm font-medium text-gray-900">Team plans include:</p>
            <ul className="space-y-1.5 text-sm text-gray-600">
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 flex-shrink-0 text-green-500" />
                Invite unlimited team members
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 flex-shrink-0 text-green-500" />
                Pooled credits shared across all users
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 flex-shrink-0 text-green-500" />
                Shared team calendar & scheduling
              </li>
            </ul>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Maybe Later</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowUpgradeAlert(false);
                navigate("/settings/billing?tab=plans");
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              View Team Plans
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}

function LocationAssignmentPicker({
  user,
  locations,
  onAssignmentChange,
  onBulkAssignmentChange,
}: {
  user: OrgUser;
  locations: any[];
  onAssignmentChange: (user: OrgUser, locationId: Id<"serviceLocations">, isAssigned: boolean) => void;
  onBulkAssignmentChange: (user: OrgUser, locationIds: Id<"serviceLocations">[]) => void;
}) {
  const [dropdownSearch, setDropdownSearch] = useState("");
  const [badgeFilter, setBadgeFilter] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const assignedLocationIds = (user.assignedLocations || []) as Id<"serviceLocations">[];
  const assignedCount = assignedLocationIds.length;
  const totalCount = locations.length;
  const isAllAssigned = totalCount > 0 && assignedCount === totalCount;

  // Filter locations for dropdown search
  const filteredDropdownLocations = locations.filter((loc: any) => {
    if (!dropdownSearch.trim()) return true;
    const query = dropdownSearch.toLowerCase();
    return (
      loc.name?.toLowerCase().includes(query) ||
      loc.address?.street?.toLowerCase().includes(query) ||
      loc.address?.city?.toLowerCase().includes(query) ||
      loc.businessName?.toLowerCase().includes(query)
    );
  });

  // Filter assigned locations for badge view
  const assignedLocationsList = locations.filter((loc: any) =>
    assignedLocationIds.includes(loc._id)
  );

  const filteredAssignedBadges = assignedLocationsList.filter((loc: any) => {
    if (!badgeFilter.trim()) return true;
    const query = badgeFilter.toLowerCase();
    return (
      loc.name?.toLowerCase().includes(query) ||
      loc.address?.city?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="mt-4 border-t border-gray-200 pt-4">
      {/* Header & Controls Bar */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-semibold text-gray-900">
              Assigned Service Locations for {user.name || user.email}
            </span>
            <Badge variant="outline" className="ml-1 font-mono text-xs text-blue-700 bg-blue-50 border-blue-200">
              {isAllAssigned ? "All assigned" : `${assignedCount} of ${totalCount} assigned`}
            </Badge>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            Members (Field Supervisors) will only be able to view and manage service locations assigned to them.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 self-start sm:self-auto">
          {/* Add / Search Dropdown Popover */}
          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8 border-blue-200 text-blue-700 hover:bg-blue-50">
                <Plus className="h-3.5 w-3.5" />
                Add Location
                <ChevronDown className="h-3 w-3 opacity-60 ml-0.5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-3" align="end">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-900">Select Service Locations</span>
                  <span className="text-[11px] text-gray-400">{filteredDropdownLocations.length} locations</span>
                </div>

                {/* Search Input */}
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
                  <Input
                    placeholder="Search by name, address, or city..."
                    value={dropdownSearch}
                    onChange={(e) => setDropdownSearch(e.target.value)}
                    className="pl-8 h-8 text-xs bg-white"
                  />
                  {dropdownSearch && (
                    <button
                      type="button"
                      onClick={() => setDropdownSearch("")}
                      className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                {/* Scrollable list */}
                <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
                  {filteredDropdownLocations.length === 0 ? (
                    <p className="p-3 text-center text-xs text-gray-400">No matching locations found.</p>
                  ) : (
                    filteredDropdownLocations.map((loc: any) => {
                      const isAssigned = assignedLocationIds.includes(loc._id);
                      return (
                        <div
                          key={loc._id}
                          onClick={() => onAssignmentChange(user, loc._id, !isAssigned)}
                          className={cn(
                            "flex items-center justify-between p-2 rounded-md text-xs cursor-pointer transition-colors",
                            isAssigned
                              ? "bg-blue-50 text-blue-900 font-medium"
                              : "hover:bg-gray-100 text-gray-700"
                          )}
                        >
                          <div className="truncate pr-2">
                            <div className="truncate font-medium">{loc.name}</div>
                            {loc.address?.city && (
                              <div className="text-[11px] text-gray-400 truncate">
                                {loc.address.city}, {loc.address.state}
                              </div>
                            )}
                          </div>
                          {isAssigned && <Check className="h-4 w-4 text-blue-600 shrink-0" />}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Select All / Clear All */}
          {assignedCount < totalCount && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-xs text-gray-600 hover:text-blue-600"
              onClick={() => onBulkAssignmentChange(user, locations.map((l: any) => l._id))}
            >
              Select All
            </Button>
          )}
          {assignedCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-xs text-red-600 hover:bg-red-50"
              onClick={() => onBulkAssignmentChange(user, [])}
            >
              Clear All
            </Button>
          )}
        </div>
      </div>

      {/* Filter bar for badges if count > 6 */}
      {assignedCount > 6 && (
        <div className="mb-2 relative w-full sm:w-64">
          <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" />
          <Input
            placeholder="Filter assigned badges..."
            value={badgeFilter}
            onChange={(e) => setBadgeFilter(e.target.value)}
            className="pl-8 h-7 text-xs bg-white"
          />
        </div>
      )}

      {/* Assigned Badges List */}
      {assignedCount === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-4 text-center bg-white">
          <MapPin className="mx-auto h-5 w-5 text-gray-400 mb-1" />
          <p className="text-xs text-gray-600 font-medium">No locations assigned yet</p>
          <p className="text-[11px] text-gray-400 mt-0.5">
            Click "+ Add Location" above to assign service locations to this field supervisor.
          </p>
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto p-2.5 bg-white rounded-lg border border-gray-200">
          {filteredAssignedBadges.map((loc: any) => (
            <Badge
              key={loc._id}
              variant="secondary"
              className="bg-blue-50 text-blue-800 border border-blue-200 gap-1.5 py-1 px-2.5 text-xs hover:bg-blue-100 transition-colors"
            >
              <MapPin className="h-3 w-3 text-blue-500 shrink-0" />
              <span className="truncate max-w-[180px]">{loc.name}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onAssignmentChange(user, loc._id, false);
                }}
                className="text-blue-400 hover:text-red-600 hover:bg-red-100 rounded-full p-0.5 transition-colors"
                title="Remove location assignment"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {filteredAssignedBadges.length === 0 && badgeFilter && (
            <span className="text-xs text-gray-400 italic py-1">No assigned locations match "{badgeFilter}"</span>
          )}
        </div>
      )}
    </div>
  );
}
